import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Dna, 
  TrendingUp, 
  TrendingDown, 
  Clock, 
  AlertCircle,
  ChevronRight,
  Lock,
  Sparkles,
  BarChart3,
  RefreshCw,
  History,
  Target,
  Zap
} from "lucide-react";
import { API_BASE } from "@/App";
import { useToast } from "@/hooks/use-toast";
import { Link } from "react-router-dom";

const PYQGenomePage = () => {
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [genomeData, setGenomeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingSubjects, setLoadingSubjects] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchSubjects();
  }, []);

  const fetchSubjects = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE}/prelims/genome/subjects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setSubjects(data.subjects || []);
    } catch (error) {
      console.error("Error fetching subjects:", error);
    } finally {
      setLoadingSubjects(false);
    }
  };

  const fetchGenomeAnalysis = async (subjectId) => {
    setLoading(true);
    setSelectedSubject(subjectId);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE}/prelims/genome/rotation/${subjectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setGenomeData(data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch genome analysis",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getProbabilityColor = (probability) => {
    if (probability >= 70) return "text-red-600 bg-red-50 border-red-200";
    if (probability >= 50) return "text-amber-600 bg-amber-50 border-amber-200";
    return "text-green-600 bg-green-50 border-green-200";
  };

  const getProbabilityBadge = (probability) => {
    if (probability >= 70) return { bg: "bg-red-500", text: "HIGH" };
    if (probability >= 50) return { bg: "bg-amber-500", text: "MEDIUM" };
    return { bg: "bg-green-500", text: "LOW" };
  };

  const TopicCard = ({ topic, type }) => {
    const probBadge = getProbabilityBadge(topic.probability_2026);
    
    return (
      <div className={`p-4 rounded-lg border ${getProbabilityColor(topic.probability_2026)} transition-all hover:shadow-md`}>
        <div className="flex items-start justify-between mb-2">
          <h4 className="font-semibold text-slate-800">{topic.topic}</h4>
          <Badge className={`${probBadge.bg} text-white text-xs`}>
            {topic.probability_2026}%
          </Badge>
        </div>
        
        <div className="grid grid-cols-3 gap-2 text-xs mb-3">
          <div className="flex items-center gap-1">
            <History className="w-3 h-3" />
            <span>Last: {topic.last_asked}</span>
          </div>
          <div className="flex items-center gap-1">
            <RefreshCw className="w-3 h-3" />
            <span>Cycle: {topic.cycle_years}yr</span>
          </div>
          <div className="flex items-center gap-1">
            <BarChart3 className="w-3 h-3" />
            <span>Asked: {topic.times_asked_15yr}x</span>
          </div>
        </div>
        
        <p className="text-xs text-slate-600 italic">
          {topic.reason}
        </p>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-slate-50 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Dna className="w-8 h-8 text-purple-600" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-800">
                PYQ Pattern Genome
              </h1>
              <p className="text-slate-600">Topic Rotation Tracker</p>
            </div>
          </div>
          <p className="text-sm text-slate-500 mt-2 max-w-2xl">
            Analyzes 15 years of UPSC Prelims patterns to identify topic rotation cycles. 
            Discover which topics are "due" for 2026 based on historical patterns.
          </p>
        </div>

        {/* Subject Selection */}
        <Card className="mb-6 border-purple-100">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Target className="w-5 h-5 text-purple-600" />
              Select Subject for Genome Analysis
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingSubjects ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-purple-600" />
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {subjects.map((subject) => (
                  <Button
                    key={subject.id}
                    variant={selectedSubject === subject.id ? "default" : "outline"}
                    className={`justify-start h-auto py-3 px-4 ${
                      selectedSubject === subject.id 
                        ? "bg-purple-600 hover:bg-purple-700" 
                        : "hover:bg-purple-50 hover:border-purple-300"
                    }`}
                    onClick={() => fetchGenomeAnalysis(subject.id)}
                    disabled={loading}
                  >
                    <div className="text-left">
                      <div className="font-medium text-sm">{subject.name}</div>
                      <div className={`text-xs ${selectedSubject === subject.id ? "text-purple-200" : "text-slate-500"}`}>
                        {subject.category}
                      </div>
                    </div>
                  </Button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Loading State */}
        {loading && (
          <Card className="border-purple-100">
            <CardContent className="py-16">
              <div className="flex flex-col items-center justify-center gap-4">
                <div className="relative">
                  <Dna className="w-12 h-12 text-purple-600 animate-pulse" />
                  <Sparkles className="w-4 h-4 text-amber-500 absolute -top-1 -right-1 animate-bounce" />
                </div>
                <p className="text-slate-600 font-medium">Analyzing 15 years of UPSC patterns...</p>
                <p className="text-xs text-slate-400">Identifying rotation cycles and correlations</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Genome Results */}
        {genomeData && !loading && (
          <div className="space-y-6">
            {/* Summary Header */}
            {genomeData.genome_summary && (
              <Card className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white border-0">
                <CardContent className="py-6">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <h2 className="text-xl font-bold mb-1">
                        {genomeData.subject?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Genome Analysis
                      </h2>
                      <p className="text-purple-200 text-sm">
                        Analysis Period: {genomeData.analysis_period}
                      </p>
                    </div>
                    <div className="flex gap-4 text-center">
                      <div>
                        <div className="text-2xl font-bold">{genomeData.genome_summary?.total_topics_analyzed || genomeData.high_probability?.length || 0}</div>
                        <div className="text-xs text-purple-200">Topics Analyzed</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-amber-300">{genomeData.genome_summary?.hot_topics_count || genomeData.high_probability?.length || 0}</div>
                        <div className="text-xs text-purple-200">Hot Topics</div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* High Probability Section */}
            {genomeData.high_probability && genomeData.high_probability.length > 0 && (
              <Card className="border-red-100">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2 text-red-700">
                    <TrendingUp className="w-5 h-5" />
                    🔴 High Probability for 2026
                    <Badge variant="destructive" className="ml-2">DUE</Badge>
                  </CardTitle>
                  <p className="text-sm text-slate-500">
                    Topics overdue based on rotation cycle analysis
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    {genomeData.high_probability.map((topic, idx) => (
                      <TopicCard key={idx} topic={topic} type="high" />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Medium Probability Section - PRO Only */}
            {genomeData.medium_probability && genomeData.medium_probability.length > 0 && (
              <Card className="border-amber-100">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2 text-amber-700">
                    <AlertCircle className="w-5 h-5" />
                    🟡 Medium Probability
                    <Badge className="bg-amber-500 ml-2">WATCH</Badge>
                  </CardTitle>
                  <p className="text-sm text-slate-500">
                    Topics with moderate chance of appearing
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    {genomeData.medium_probability.map((topic, idx) => (
                      <TopicCard key={idx} topic={topic} type="medium" />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recently Asked Section - PRO Only */}
            {genomeData.recently_asked && genomeData.recently_asked.length > 0 && (
              <Card className="border-green-100">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2 text-green-700">
                    <TrendingDown className="w-5 h-5" />
                    🟢 Recently Asked (Low Probability)
                    <Badge className="bg-green-500 ml-2">SKIP</Badge>
                  </CardTitle>
                  <p className="text-sm text-slate-500">
                    Topics unlikely to repeat immediately
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {genomeData.recently_asked.map((topic, idx) => (
                      <TopicCard key={idx} topic={topic} type="low" />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Pattern Insights - PRO Only */}
            {genomeData.pattern_insights && genomeData.pattern_insights.length > 0 && (
              <Card className="border-indigo-100">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2 text-indigo-700">
                    <Zap className="w-5 h-5" />
                    🧬 Cross-Topic Patterns
                  </CardTitle>
                  <p className="text-sm text-slate-500">
                    Hidden correlations between topics
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {genomeData.pattern_insights.map((pattern, idx) => (
                      <div key={idx} className="p-4 bg-indigo-50 rounded-lg border border-indigo-100">
                        <h4 className="font-semibold text-indigo-800 mb-1">{pattern.pattern}</h4>
                        <p className="text-sm text-indigo-600 mb-2">{pattern.correlation}</p>
                        <p className="text-xs text-slate-600 italic">
                          💡 {pattern.implication}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Upgrade Prompt for FREE users */}
            {genomeData.upgrade_message && (
              <Card className="bg-gradient-to-r from-slate-800 to-slate-900 text-white border-0">
                <CardContent className="py-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-amber-500 rounded-full">
                      <Lock className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold mb-1">Unlock Complete Genome Analysis</h3>
                      <p className="text-sm text-slate-300">{genomeData.upgrade_message}</p>
                    </div>
                    <Link to="/upgrade">
                      <Button className="bg-amber-500 hover:bg-amber-600 text-black font-semibold">
                        Upgrade to PRO
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Initial State - No Subject Selected */}
        {!genomeData && !loading && (
          <Card className="border-dashed border-2 border-purple-200 bg-purple-50/50">
            <CardContent className="py-16">
              <div className="text-center">
                <Dna className="w-16 h-16 text-purple-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">
                  Select a Subject to Begin
                </h3>
                <p className="text-sm text-slate-500 max-w-md mx-auto">
                  Choose a subject above to analyze its PYQ pattern genome and discover 
                  which topics are most likely to appear in 2026.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default PYQGenomePage;
